/** @jsx jsx */
import React from 'react'
import styled from '@emotion/styled'
import {css, jsx, keyframes} from '@emotion/core'
import {Optional} from 'utility-types'

type PropsType = {
  /** The fill percentage of the bar */
  value: number
  /**  The progressbar appearance. We use this as a flag for the StepBar component **/
  appearance?: 'default' | 'stepBar' | 'ratings'
  /** The content inside the progressbar. Used with StepBar component to place the circle and caption inside the bar **/
  children?: React.ReactNode
}

const defaultProgressBarStyles = ({theme, appearance, value}) => {
  return css`
    display: block;
    height: 6px;
    border-radius: ${theme.layout.radius.xs}px;
    padding: 0;
    background-color: ${barBg({theme, value, appearance})};
  `
}

const stepBarProgressBarStyles = ({theme, appearance, value}) => {
  return css`
    display: inline-flex;
    align-items: center;
    height: ${theme.layout.spacing.s400}px;
    border-radius: ${theme.layout.radius.lg}px;
    padding: ${theme.layout.spacing.s200}px;
    background-color: ${barBg({theme, value, appearance})};
  `
}

const horizontalProgressBarElementDynamicStyles = ({appearance}) => {
  return appearance === 'stepBar'
    ? stepBarProgressBarStyles
    : defaultProgressBarStyles
}

const horizontalProgressBarElementBaseStyles = () => css`
  width: 100%;
`

const HorizontalProgressBarElement = styled.div<Optional<PropsType, 'value'>>(
  horizontalProgressBarElementBaseStyles,
  horizontalProgressBarElementDynamicStyles
)

const horizontalProgressBarValueBaseStyles = ({value}) => css`
  width: ${value}%;
`

const horizontalProgressBarValueDynamicStyles = ({
  theme,
  value,
  appearance
}) => {
  return css`
    background-color: ${barColor({theme, value, appearance})};
    border-radius: 3px;
    z-index: 1;
    height: ${appearance === 'stepBar'
      ? `${theme.layout.spacing.s200}px`
      : '100%'};
  `
}

const HorizontalProgressBarValue = styled.div<PropsType>(
  horizontalProgressBarValueBaseStyles,
  horizontalProgressBarValueDynamicStyles
)

const barBg = ({theme, value, appearance}) => {
  if (appearance === 'stepBar') {
    return theme.colors.violet.v200
  }

  if (appearance === 'ratings') {
    return theme.colors.borderColor
  }

  // default behaviour
  if (value >= 30) {
    return theme.colors.qualityNice
  }

  if (value >= 10) {
    return theme.colors.qualityOkay
  }

  return theme.colors.qualityPoor
}

const barColor = ({theme, value, appearance}) => {
  if (appearance === 'stepBar') {
    return theme.colors.privateDealColor
  }

  if (appearance === 'ratings') {
    if (value < 50) {
      return theme.colors.qualityPoorBg
    }

    if (value < 60) {
      return theme.colors.qualityAverageBg
    }

    if (value < 70) {
      return theme.colors.qualityOkayBg
    }

    if (value < 80) {
      return theme.colors.qualityGoodBg
    }

    if (value < 90) {
      return theme.colors.qualityNiceBg
    }

    return theme.colors.qualityExcellentBg
  }

  // default behaviour
  if (value >= 30) {
    return theme.colors.qualityNiceBg
  }

  if (value >= 10) {
    return theme.colors.qualityOkayBg
  }

  return theme.colors.qualityPoorBg
}

const HorizontalProgressBar = ({
  value,
  appearance = 'default',
  children
}: PropsType) => {
  const slideFromLeft = keyframes`
      0% {
        width: 0;
      }

      100% {
        width: ${value}%;
      }
    `
  return (
    <HorizontalProgressBarElement value={value} appearance={appearance}>
      <HorizontalProgressBarValue
        value={value}
        appearance={appearance}
        css={css`
          animation: ${slideFromLeft} 0.6s ease;
        `}
      />
      {children}
    </HorizontalProgressBarElement>
  )
}

export default HorizontalProgressBar
